import { ng } from "@common/ngds";
import { IDisplacement, ISectionForce } from "@common/types/sharedTypes";
import { diagramPadding, Drawer, Point, ValueType } from "@common/components/Rendering";
import { GraphDirection } from "./SideView";
import {
  moment_fill_color_style,
  shear_fill_color_style,
  normal_fill_color_style,
  deformation_fill_color_style,
  moment_line_color_style,
  shear_line_color_style,
  normal_line_color_style,
  deformation_line_color_style,
  moment_zero_line_style,
  shear_zero_line_style,
  normal_zero_line_style,
  zero_line_style,
  graph_point_style,
  label_style,
  diagram_header_style,
  viewportColors,
  ArrowId,
} from "../ViewportStyles";

import {
  IReactionForceDictionary,
  ISectionForceDictionary,
  IDisplacementDictionary,
  IDesignResultDictionary,
} from "@common/types/sharedTypes";

export interface DiagramResults {
  elementForces: ISectionForceDictionary;
  supportForces: IReactionForceDictionary;
  displacements: IDisplacementDictionary;
  // The 1st order counterpart of elementForces/displacements, only populated by App when both the
  // 1st and 2nd order result diagrams are shown at once. When present, DrawGraph draws it as a
  // faded reference curve (no labels) behind the primary one - see DrawGraph's `secondary*` params.
  elementForcesSecondary?: ISectionForceDictionary;
  displacementsSecondary?: IDisplacementDictionary;
  bucklingMode: number[][];
  design: IDesignResultDictionary;
  betas: number[][];
}

type GraphPointsMode = "off" | "max" | "all";

export type DiagramType =
  | "M" // moment
  | "V" // shear
  | "N" // axial
  | "D"; // deformation

export type DiagramValueRanges = Partial<Record<DiagramType, number>>;

// Unit scales and zero thresholds for the plotted quantities. Hoisted out of CreateGraphValues
// so secondOrderDiagramValueRanges below measures in exactly the same units it draws in.
const FORCE_VALUE_SCALE = 1.0e-3; // [kN] / [kNm]
const FORCE_ZERO = 1.0e-1; // [kN/kNm] a curve whose every value is below this is drawn as zero
const DISPLACEMENT_VALUE_SCALE = 1.0e3; // [mm]
const DISPLACEMENT_ZERO = 1.0e-2; // [mm] a curve whose every value is below this is drawn as zero

// Horizontal room reserved on each side of a lane, past the +/-1 a normalised curve can reach,
// for the value label drawn just outside the extreme point. See drawDiagramExtentAnchors.
const diagramLabelReserve = 0.7;

// Invisible, but a real drawable: it is meant to be counted by the auto-fit bounding box and by
// nothing else.
const extent_anchor_style = { lineColor: "transparent", thickness: 0 };

// EXPERIMENT - drawing direction per diagram type. This is the ONLY place where a diagram is
// allowed to deviate from the raw analysis data, and it deviates in the DRAWING only:
//
//   +1  the curve bulges to the side the raw value puts it on (baseline, no manipulation)
//   -1  the curve is MIRRORED about its own zero axis
//
// The printed LABEL is always the raw analysis value regardless of this setting - the label sign
// is derived from it (see labelSign below), so what is written next to a data point is the same
// number, with the same sign, that the code check reports. Only the side the curve (and with it
// its label) sits on changes. Label placement follows the drawn curve, so labels stay on the
// outside of their own curve either way.
//
// Set every entry to 1 to get the pure baseline back.
const DRAW_SIGN: Record<DiagramType, 1 | -1> = {
  M: 1, // moment: drawn from raw M2 (xy) / M1 (xz)
  V: -1, // shear: mirrored
  N: 1, // axial: drawn from raw N
  D: -1, // deflection: mirrored
};

// One piece of a diagram header, e.g. the "M", the subscript "y" and the unit "[kNm]" of "My [kNm]".
type HeaderPart = {
  text: string;
  subscript?: boolean; // drawn smaller and lowered
  spaceBefore?: boolean; // separated from the previous part by a space
};

const headerSubscriptScale = 0.75; // font size of a subscript relative to the header font
const headerSubscriptDrop = 0.18; // how far a subscript is lowered, as a fraction of the header font size

// Advance width per character, in em, for the few characters diagram headers are made of. The
// header is laid out by hand (the renderer cannot measure a text up front), so the widths are
// needed to place the parts of a header next to each other. Values are the usual sans-serif
// advances rounded UP, with a safety margin on top - an overestimate only widens the gap between
// two parts, an underestimate would let them overlap.
const headerCharWidths: Record<string, number> = {
  M: 0.84,
  N: 0.73,
  V: 0.67,
  d: 0.56,
  k: 0.5,
  m: 0.84,
  y: 0.5,
  z: 0.5,
  "[": 0.28,
  "]": 0.28,
  " ": 0.28,
};
const headerTextWidth = (text: string, fontSize: number): number => {
  let em = 0;
  for (const character of text) {
    em += headerCharWidths[character] ?? 0.6; // fallback for a character that is not in the table
  }
  return em * fontSize * 1.05; // safety margin, the actual font may be slightly wider
};

// The header of one diagram type. The moment is about the y axis when looking along xz and about
// the z axis when looking along xy, so its header is "My [kNm]" respectively "Mz [kNm]".
const headerPartsForType = (type: DiagramType, direction: GraphDirection): HeaderPart[] => {
  if (type === "M") {
    return [{ text: "M" }, { text: direction === "xz" ? "y" : "z", subscript: true }, { text: "[kNm]", spaceBefore: true }];
  }
  if (type === "V") return [{ text: "V" }, { text: "[kN]", spaceBefore: true }];
  if (type === "N") return [{ text: "N" }, { text: "[kN]", spaceBefore: true }];
  return [{ text: "d" }, { text: "[mm]", spaceBefore: true }];
};

/** One run of a laid-out diagram header, positioned in pixels relative to the lane's header anchor. */
export interface DiagramHeaderRun {
  text: string;
  color: string;
  fontSize: number;
  /** pixels right of the anchor to the run's left edge */
  horiOffset: number;
  /** pixels the run is lowered from the anchor's bottom-aligned line (subscripts only) */
  vertOffset: number;
}

/**
 * Lays out the header of one lane - one segment per diagram type in it, each in its own colour -
 * centred on the anchor, with the run texts bottom-aligned on it. Shared by the 2D side view and
 * both isometric views so the headers look the same everywhere.
 *
 * Everything is placed with PIXEL offsets, not world coordinates: the font size is in pixels, so a
 * world-unit layout would only hold at one camera zoom and the texts could end up on top of each
 * other at any other. Widths come from headerTextWidth (advance widths with a safety margin), which
 * keeps the parts of a header adjacent - never overlapping - and the segments of a lane separated
 * by a gap.
 */
export const layoutDiagramHeader = (
  segments: Array<{ type: DiagramType; color: string }>,
  direction: GraphDirection,
  fontSize: number,
): DiagramHeaderRun[] => {
  const segmentGap = 0.5 * fontSize; // pixel gap between two headers sharing a lane
  const spaceWidth = 0.3 * fontSize; // pixel gap in front of a unit, i.e. the space in "N [kN]"
  const partFontSize = (part: HeaderPart): number => (part.subscript ? fontSize * headerSubscriptScale : fontSize);
  const partWidth = (part: HeaderPart): number =>
    (part.spaceBefore ? spaceWidth : 0) + headerTextWidth(part.text, partFontSize(part));

  const segmentParts = segments.map((segment) => headerPartsForType(segment.type, direction));
  const segmentWidths = segmentParts.map((parts) => parts.reduce((sum, part) => sum + partWidth(part), 0));
  const totalWidth = segmentWidths.reduce((sum, width) => sum + width, 0) + segmentGap * (segments.length - 1);

  const runs: DiagramHeaderRun[] = [];
  let segmentStart = -totalWidth / 2;
  segments.forEach((segment, i) => {
    let partStart = segmentStart;
    for (const part of segmentParts[i]) {
      runs.push({
        text: part.text,
        color: segment.color,
        fontSize: partFontSize(part),
        horiOffset: partStart + (part.spaceBefore ? spaceWidth : 0),
        // a subscript is nudged down so it sits below the letter it belongs to
        vertOffset: part.subscript ? headerSubscriptDrop * fontSize : 0,
      });
      partStart += partWidth(part);
    }
    segmentStart += segmentWidths[i] + segmentGap;
  });
  return runs;
};

const DrawGraph = (
  g: Drawer<ArrowId> | null,
  resultPositions: ISectionForce[],
  displacements: IDisplacement[],
  direction: GraphDirection,
  diagramType: DiagramType[],
  graphPointsMode: GraphPointsMode,
  scale: number,
  visible: boolean,
  horOffset: number,
  sharedValueRanges?: DiagramValueRanges,
  // The 1st order counterpart of resultPositions/displacements, only passed when both the 1st and
  // 2nd order results are shown at once (App's showBothOrders). Drawn as a faded reference curve,
  // on the same lane and scale as the primary curve, with no labels and no graph points.
  secondaryResultPositions?: ISectionForce[],
  secondaryDisplacements?: IDisplacement[],
): DiagramValueRanges => {
  const diagramValueRanges: DiagramValueRanges = {};
  let nbrOfResults = resultPositions ? resultPositions.length : 0;
  if (nbrOfResults === 0) {
    return diagramValueRanges;
  }

  const hasNormal = diagramType.includes("N");
  const hasMomentOrShear = diagramType.includes("M") || diagramType.includes("V");
  const hasDeformation = diagramType.includes("D");

  // Group diagram types into horizontal lanes so N stays left, M/V share one lane, and D is separate.
  const activeLanes: Array<"N" | "MV" | "D"> = [];
  if (hasNormal) {
    activeLanes.push("N");
  }
  if (hasMomentOrShear) {
    activeLanes.push("MV");
  }
  if (hasDeformation) {
    activeLanes.push("D");
  }

  // M and V intentionally return the same lane index to overlay their curves and save horizontal space.
  const laneIndexForType = (type: DiagramType): number => {
    if (type === "N") {
      return Math.max(0, activeLanes.indexOf("N"));
    }
    if (type === "M" || type === "V") {
      return Math.max(0, activeLanes.indexOf("MV"));
    }
    return Math.max(0, activeLanes.indexOf("D"));
  };

  // Lane a diagram type is drawn in. M and V share a lane, so they also share one header.
  const laneKeyForType = (type: DiagramType): "N" | "MV" | "D" => (type === "N" ? "N" : type === "D" ? "D" : "MV");

  // Header text drawn above each lane, e.g. "N [kN]". A lane holds one segment per diagram type in
  // it (the M/V lane gets two, each in its own color), collected here and drawn after all curves
  // through layoutDiagramHeader, which the isometric views share.
  const laneHeaders = new Map<
    "N" | "MV" | "D",
    { laneX: number; segments: Array<{ type: DiagramType; color: string }> }
  >();
  // Screen y of the top of the member. y grows downwards, so the top is the smallest y of all
  // graph points; all headers share it so they line up with each other.
  let diagramTopY = 0;

  // Collect all text labels to draw them after all curves, ensuring text is always on top.
  const deferredLabels: Array<{
    point: Point;
    graphPoint: Point; // the actual point on the graph the label belongs to (without label offsets)
    laneX: number; // x of the diagram zero axis (insertPoint[0]) the graph point is drawn around
    text: string;
    horiAlign: "left" | "right";
    vertAlign: "top" | "middle" | "bottom";
    type: DiagramType;
  }> = [];

  for (const type of diagramType) {
    const insertPoint = [horOffset + diagramPadding * laneIndexForType(type), 0] as Point;
    const baseFillStyle =
      type === "M"
        ? moment_fill_color_style
        : type === "V"
          ? shear_fill_color_style
          : type === "N"
            ? normal_fill_color_style
            : deformation_fill_color_style;
    const baseLineStyle =
      type === "M"
        ? moment_line_color_style
        : type === "V"
          ? shear_line_color_style
          : type === "N"
            ? normal_line_color_style
            : deformation_line_color_style;
    const isOverlayedMomentOrShear =
      (type === "M" || type === "V") && diagramType.includes("M") && diagramType.includes("V");
    const makeTransparentLine = (color: string | undefined, visiblePercent: number): string | undefined =>
      color ? `color-mix(in srgb, ${color} ${visiblePercent}%, transparent)` : color;
    const makeTransparentFill = (color: string | null, visiblePercent: number): string | null =>
      color ? `color-mix(in srgb, ${color} ${visiblePercent}%, transparent)` : color;
    const fillStyle =
      isOverlayedMomentOrShear && baseFillStyle
        ? {
            fill: makeTransparentFill(baseFillStyle.fill, 45),
          }
        : baseFillStyle;
    const lineStyle = isOverlayedMomentOrShear
      ? {
          ...baseLineStyle,
          lineColor: makeTransparentLine(baseLineStyle.lineColor, 65),
        }
      : baseLineStyle;
    const zeroStyle =
      type === "M"
        ? moment_zero_line_style
        : type === "V"
          ? shear_zero_line_style
          : type === "N"
            ? normal_zero_line_style
            : zero_line_style;

    const coordinates = CreateGraphValues(resultPositions, displacements, direction, type, scale);
    if (coordinates.length === 0) {
      continue;
    }

    const absValues = coordinates.map((c) => Math.abs(c.verticalValue)).filter(Number.isFinite);
    const maxAbsValue = absValues.length > 0 ? Math.max(...absValues) : 0;
    diagramValueRanges[type] = maxAbsValue;

    // A null drawer is the sizing pass. It deliberately uses the same graph-value generation as
    // rendering, so both side views use ranges derived from exactly the values they display.
    if (g === null) {
      continue;
    }

    // Join double points: two data points within a few mm of each other along the member that carry
    // almost the same value are replaced by a single data point - the one with the higher absolute
    // value. This joins the actual graph points (curve and drawn circles) as well as the label.
    // "Almost the same value" means within 1% of each other (so large values that differ by a
    // fraction of a percent are collapsed instead of being drawn as a double point). If the values
    // differ by more (e.g. a jump caused by a point load), both data points are kept.
    // The distance window is deliberately wider than the label-join window (2.1 mm * 1.5) further
    // down, so an equal-value pair is always REDUCED here (rule 1) and never ends up as a joined
    // "x, x" label (rule 2 is only for real jumps). Real output positions are never closer than
    // ~50 mm, so the window cannot swallow two genuinely distinct points.
    if (graphPointsMode === "all") {
      const doublePointDist = 0.0032 * scale; // 3.2 mm in drawing units
      const doublePointRelTol = 0.01; // values within 1% of each other are considered "almost the same"

      for (let i = 0; i + 1 < coordinates.length; i++) {
        const c0 = coordinates[i];
        const c1 = coordinates[i + 1];

        const valueDiff = Math.abs(c1.verticalValue - c0.verticalValue);
        const maxAbs = Math.max(Math.abs(c0.verticalValue), Math.abs(c1.verticalValue));

        if (
          Math.abs(c1.horizontalValue - c0.horizontalValue) < doublePointDist &&
          valueDiff < maxAbs * doublePointRelTol &&
          // Ignore zero values and never merge a pair whose values have different signs.
          // The product is only positive when both values are non-zero and share a sign.
          c0.verticalValue * c1.verticalValue > 0
        ) {
          // Keep the point with the higher absolute value (its actual position and value).
          const kept = Math.abs(c0.verticalValue) >= Math.abs(c1.verticalValue) ? c0 : c1;
          coordinates.splice(i, 2, kept);
          // Re-check the merged point against the next one, so a CLUSTER of coincident
          // points (e.g. 3-4 outputs at a node where a distributed load boundary sits)
          // collapses into a single point instead of leaving a leftover pair.
          i--;
        }
      }
    }

    const maxVerticalValue = eval(Math.max(...coordinates.map((c) => c.verticalValue)).toFixed(3));
    const minVerticalValue = eval(Math.min(...coordinates.map((c) => c.verticalValue)).toFixed(3));

    const maxValueIndexes = coordinates
      .map((c, i) => (eval(c.verticalValue.toFixed(3)) === maxVerticalValue ? i : -1))
      .filter((i) => i !== -1);

    const minValueIndexes = coordinates
      .map((c, i) => (eval(c.verticalValue.toFixed(3)) === minVerticalValue ? i : -1))
      .filter((i) => i !== -1);

    // Among the indexes sharing the extreme value, prefer one at a key position along the
    // member: 0, L/2 or L (with some padding). Falls back to the first occurrence.
    const memberLength = coordinates[coordinates.length - 1].horizontalValue;
    const keyPositionPadding = 0.02 * memberLength;
    const keyPositions = [0, memberLength / 2, memberLength];
    const pickPreferredIndex = (indexes: number[]): number => {
      if (indexes.length === 0) {
        return -1;
      }
      for (const key of keyPositions) {
        const match = indexes.find((i) => Math.abs(coordinates[i].horizontalValue - key) <= keyPositionPadding);
        if (match !== undefined) {
          return match;
        }
      }
      return indexes[0];
    };

    const maxValueIndex = pickPreferredIndex(maxValueIndexes);
    const minValueIndex = pickPreferredIndex(minValueIndexes);
    const maxCoordinate = coordinates[Math.max(0, maxValueIndex)];
    const minCoordinate = coordinates[Math.max(0, minValueIndex)];

    // SideView's sizing pass collects one range per diagram type over all load combinations and
    // both view directions, so the range handed in here should already cover these values. The
    // clamp is a safety net for any caller that passes a narrower range: without it, a range near
    // zero - a column with no or very small normal force gives max |N| ~ 0 - divides into
    // maxAbs / ~0 and puts the curve hundreds of drawing units outside its lane. Hidden drawables
    // are skipped when rendering but are still counted by the auto-fit bounding box, so the camera
    // would zoom out to fit them and every visible diagram would shrink to nothing.
    // Clamping guarantees graphScale <= 1, so a curve can never leave its own +/-1 lane.
    const sharedRange = sharedValueRanges?.[type];
    const verticalValueRange = sharedRange !== undefined && sharedRange >= maxAbsValue ? sharedRange : maxAbsValue;
    const effectiveValueRange = verticalValueRange > 0 ? verticalValueRange : 1;
    const graphScale = maxAbsValue > 0 ? maxAbsValue / effectiveValueRange : 1;
    const pointOnGraph = (coordinate: ValueType): Point => [
      insertPoint[0] - coordinate.verticalValue / effectiveValueRange,
      insertPoint[1] - coordinate.horizontalValue,
    ];

    // The header of this lane is centered over the lane's zero axis (insertPoint[0]) - the dashed
    // line where the graph value is zero. It uses the full curve color (baseLineStyle), not the
    // faded overlay color used when M and V share a lane.
    const laneKey = laneKeyForType(type);
    const laneHeader = laneHeaders.get(laneKey);
    const headerColor = baseLineStyle.lineColor ?? diagram_header_style.lineColor ?? viewportColors.black;
    if (laneHeader) {
      laneHeader.segments.push({ type, color: headerColor });
    } else {
      laneHeaders.set(laneKey, {
        laneX: insertPoint[0],
        segments: [{ type, color: headerColor }],
      });
    }
    diagramTopY = Math.min(diagramTopY, ...coordinates.map((c) => pointOnGraph(c)[1]));

    const isConstantDiagram = maxVerticalValue === minVerticalValue;

    // Undo the drawing direction so the printed value is the raw analysis value again. With
    // DRAW_SIGN = +1 this is a no-op; with -1 the curve is mirrored but the number is unchanged.
    const labelSign = DRAW_SIGN[type];

    const labelOffsetX = 0.08; //type === "D" ? 0.015 : 0.08;

    // Place the label on the given side of a data point, at the same vertical level.
    // A label placed to the RIGHT of the point is left-adjusted (text extends to the right,
    // away from the point) and a label placed to the LEFT is right-adjusted (text extends to
    // the left, away from the point).
    const placeLabel = (graphPoint: Point, side: "left" | "right") => ({
      point: [graphPoint[0] + (side === "right" ? labelOffsetX : -labelOffsetX), graphPoint[1]] as Point,
      horiAlign: side === "right" ? ("left" as const) : ("right" as const),
    });

    // Negative values: label to the right of the data point, positive values: to the left.
    // This uses the DRAWN value, so the label always lands on the outside of its own curve - also
    // for a diagram type that is mirrored by DRAW_SIGN. The rule is the same for all diagram
    // types; there is no per-type exception.
    const defaultLabelSide = (coordinate: ValueType): "left" | "right" =>
      coordinate.verticalValue < 0 ? "right" : "left";

    const labelPlacement = (coordinate: ValueType) => {
      const graphPoint = pointOnGraph(coordinate);
      const placed = placeLabel(graphPoint, defaultLabelSide(coordinate));
      return {
        point: placed.point,
        graphPoint,
        horiAlign: placed.horiAlign,
      };
    };

    const maxLabel = labelPlacement(maxCoordinate);
    const minLabel = labelPlacement(minCoordinate);

    const diagramPointStyle = graph_point_style
      ? {
          radius: graph_point_style.radius,
          color: {
            lineColor: lineStyle.lineColor,
            thickness: graph_point_style.color.thickness,
            fill: fillStyle?.fill ?? lineStyle.lineColor ?? graph_point_style.color.fill,
          },
        }
      : null;

    // Graph point behavior:
    // - off: no circles
    // - all: draw circles for every graph point via drawGraph
    // - max: draw only key extrema circles manually below
    const graphPointStyle = graphPointsMode === "all" ? diagramPointStyle : null;

    g.drawGraph(
      insertPoint,
      "vertical",
      graphScale,
      coordinates,
      {
        fill: fillStyle,
        line: lineStyle,
        zero: zeroStyle,
      },
      {
        point_style: graphPointStyle,
        display: visible,
      },
    );

    // Overlay: the faded 1st order reference curve, same lane and scale as the curve just drawn.
    // No labels, no graph points, no zero-line style of its own - it is a visual comparison only.
    if (secondaryResultPositions && secondaryResultPositions.length > 0) {
      const secondaryCoordinates = CreateGraphValues(
        secondaryResultPositions,
        secondaryDisplacements ?? [],
        direction,
        type,
        scale,
      );
      const secondaryAbsValues = secondaryCoordinates.map((c) => Math.abs(c.verticalValue)).filter(Number.isFinite);
      const secondaryMaxAbsValue = secondaryAbsValues.length > 0 ? Math.max(...secondaryAbsValues) : 0;
      if (secondaryCoordinates.length > 0 && secondaryMaxAbsValue > 0) {
        // Clamped like the primary curve above: a 1st order value should never exceed the shared
        // range (2nd order is expected to be the larger of the two), but this keeps the faded
        // curve inside its own lane even if that assumption does not hold for some position.
        const secondaryGraphScale = Math.min(secondaryMaxAbsValue / effectiveValueRange, 1);
        g.drawGraph(
          insertPoint,
          "vertical",
          secondaryGraphScale,
          secondaryCoordinates,
          {
            fill: fillStyle ? { fill: makeTransparentFill(fillStyle.fill, 25) } : fillStyle,
            line: { ...lineStyle, lineColor: makeTransparentLine(lineStyle.lineColor, 40) },
            // Reuses the primary curve's zero-line style: it is drawn at the same position either
            // way, so there is nothing to distinguish, and StyleForGraphZeroLine (unlike fill) does
            // not accept null.
            zero: zeroStyle,
          },
          {
            point_style: null,
            display: visible,
          },
        );
      }
    }

    if (graphPointsMode === "max" && diagramPointStyle) {
      // Use saved coordinate references (not indices) because drawGraph mutates the coordinates
      // array by splicing in zero-crossing points, which shifts indices.
      const candidates = maxCoordinate === minCoordinate ? [maxCoordinate] : [maxCoordinate, minCoordinate];

      for (const coordinate of candidates) {
        if (Math.abs(coordinate.verticalValue) < 1e-9) {
          continue;
        }
        g.drawCircle(pointOnGraph(coordinate), diagramPointStyle.radius, diagramPointStyle.color, { display: visible });
      }
    }

    // Collect label draw calls to render in a second pass (after all curves) so text is never obscured.
    // The label is the raw analysis value (DRAW_SIGN undone), so it matches the code check.
    const formatLabel = (value: number) => {
      const text = (value * labelSign).toFixed(2);
      return text === "-0.00" ? "0.00" : text;
    };
    if (graphPointsMode === "all") {
      // Double points with almost the same value have already been joined into a single averaged
      // data point right after CreateGraphValues, so every remaining coordinate gets its own label.
      for (const coordinate of coordinates) {
        const text = formatLabel(coordinate.verticalValue);
        // Keep the data point (curve and circle) but don't write a label for a zero value.
        if (text === "0.00") {
          continue;
        }
        const label = labelPlacement(coordinate);
        deferredLabels.push({
          point: label.point,
          graphPoint: label.graphPoint,
          laneX: insertPoint[0],
          text,
          horiAlign: label.horiAlign,
          vertAlign: "middle" as const,
          type,
        });
      }
    } else if (graphPointsMode === "max") {
      const maxLabelText = formatLabel(maxCoordinate.verticalValue);
      const minLabelText = formatLabel(minCoordinate.verticalValue);
      const shouldDrawMaxLabel = maxLabelText !== "0.00";
      const shouldDrawMinLabel = !isConstantDiagram && minLabelText !== "0.00";

      if (shouldDrawMaxLabel) {
        deferredLabels.push({
          point: maxLabel.point,
          graphPoint: maxLabel.graphPoint,
          laneX: insertPoint[0],
          text: maxLabelText,
          horiAlign: maxLabel.horiAlign,
          vertAlign: "middle" as const,
          type,
        });
      }
      if (shouldDrawMinLabel) {
        deferredLabels.push({
          point: minLabel.point,
          graphPoint: minLabel.graphPoint,
          laneX: insertPoint[0],
          text: minLabelText,
          horiAlign: minLabel.horiAlign,
          vertAlign: "middle" as const,
          type,
        });
      }
    }
  }

  if (g === null) {
    return diagramValueRanges;
  }

  // Resolve collisions between M and V labels: both types share the same horizontal lane, so their
  // labels can land on (nearly) the same spot. When an M label and a V label are too close, the
  // label whose data point has the smallest ABSOLUTE horizontal value (closest to the diagram zero
  // axis) is moved to the other side of its own data point; the other label is left untouched.
  // The decision is made from the ACTUAL graph points, not the label anchors. Vertical positions
  // are never changed.
  // Anchor x for a label placed on the given side of its own data point.
  const labelAnchorX = (label: (typeof deferredLabels)[number], side: "left" | "right"): number => {
    const offsetX = 0.08; //label.type === "D" ? 0.015 : 0.08;
    return label.graphPoint[0] + (side === "right" ? offsetX : -offsetX);
  };

  // Approximate extent of a label text box (in drawing units) for collision checks. The width is
  // estimated from the number of characters, so long labels (e.g. the joined double point values
  // "12.00, 17.00") cover a correspondingly larger box.
  const labelCharWidth = 0.042; // ~0.25 for a typical 6-character label like "-12.00"
  const labelTextHalfHeight = 0.02;
  const labelWidth = (label: (typeof deferredLabels)[number]): number => label.text.length * labelCharWidth;

  // The x-interval covered by a label's text: a left-adjusted label extends to the right of its
  // anchor, a right-adjusted label extends to the left of it.
  const labelXInterval = (label: (typeof deferredLabels)[number]): [number, number] =>
    label.horiAlign === "left"
      ? [label.point[0], label.point[0] + labelWidth(label)]
      : [label.point[0] - labelWidth(label), label.point[0]];

  // Current side of a label (horiAlign "left" means the label sits on the right side of its point).
  const currentSide = (label: (typeof deferredLabels)[number]): "left" | "right" =>
    label.horiAlign === "left" ? "right" : "left";

  // Check whether a label, anchored on the given side of its data point, would collide with
  // another data point. The text extends away from the point: to the right when placed on the
  // right side (left-adjusted), to the left when placed on the left side (right-adjusted).
  // `avoidCircle` selects WHICH data points count in this phase (e.g. only V/M points, or only
  // N/D points). An optional label can be excluded from the check (double point partners).
  const collidesWithDataPoint = (
    label: (typeof deferredLabels)[number],
    side: "left" | "right",
    avoidCircle: (mover: (typeof deferredLabels)[number], obstacle: (typeof deferredLabels)[number]) => boolean,
    exclude?: (typeof deferredLabels)[number],
  ): boolean => {
    const x = labelAnchorX(label, side);
    const w = labelWidth(label);
    const r = graph_point_style?.radius ?? 0; // the data point CIRCLE, not just its centre
    const xMin = side === "right" ? x : x - w;
    const xMax = side === "right" ? x + w : x;
    for (const other of deferredLabels) {
      if (other === label || other === exclude) {
        continue;
      }
      if (!avoidCircle(label, other)) {
        continue; // this phase does not avoid this data point
      }
      if (other.graphPoint[0] === label.graphPoint[0] && other.graphPoint[1] === label.graphPoint[1]) {
        continue; // same data point
      }
      if (
        other.graphPoint[0] >= xMin - r &&
        other.graphPoint[0] <= xMax + r &&
        Math.abs(other.graphPoint[1] - label.graphPoint[1]) < labelTextHalfHeight + r
      ) {
        return true;
      }
    }
    return false;
  };

  // Snap a label to the given side of its own data point (left side = right-adjusted text
  // extending to the left, right side = left-adjusted text extending to the right).
  // Returns true if anything changed.
  const snapLabelToSide = (label: (typeof deferredLabels)[number], side: "left" | "right"): boolean => {
    const x = labelAnchorX(label, side);
    const horiAlign = side === "right" ? ("left" as const) : ("right" as const);
    const changed = label.point[0] !== x || label.horiAlign !== horiAlign;
    label.point[0] = x;
    label.horiAlign = horiAlign;
    return changed;
  };

  // The x-interval a label's text would cover on the given side of its data point.
  const intervalForSide = (label: (typeof deferredLabels)[number], side: "left" | "right"): [number, number] => {
    const x = labelAnchorX(label, side);
    const w = labelWidth(label);
    return side === "right" ? [x, x + w] : [x - w, x];
  };

  // Gap enforcement (4i): every label is placed with a gap to the NEAREST data point - its own
  // or one from another data set. If a data point (circle) intrudes into the label's text box,
  // the label is pushed further out in its text direction until it sits one standard gap from
  // the outermost offending point. Sides are never changed here; the label only moves outward.
  // This runs after every collision iteration (so the gap also holds right after a side switch)
  // and once more as a final pass.
  const labelPointGap = 0.08; // same gap as from the label's own data point
  const enforceGapToDataPoints = (
    movable: (l: (typeof deferredLabels)[number]) => boolean,
    avoidCircle: (mover: (typeof deferredLabels)[number], obstacle: (typeof deferredLabels)[number]) => boolean,
  ): boolean => {
    const pointRadius = graph_point_style?.radius ?? 0;
    let anyMoved = false;

    for (const label of deferredLabels) {
      if (label.text === "" || !movable(label)) {
        continue;
      }
      const extendsRight = label.horiAlign === "left";

      // pushing the label out may expose new conflicts further out, so repeat until clear
      for (let guard = 0; guard < 10; guard++) {
        const [x0, x1] = labelXInterval(label);

        let pushTo: number | null = null;
        for (const other of deferredLabels) {
          const gp = other.graphPoint;
          if (other === label) {
            continue; // skip the label's own entry; anchor offset already guarantees its gap
          }
          if (!avoidCircle(label, other)) {
            continue; // this phase does not keep a gap to this data point
          }
          if (Math.abs(gp[1] - label.graphPoint[1]) >= labelTextHalfHeight + pointRadius) {
            continue;
          }
          // Leading edge needs a full labelPointGap; trailing edge only needs pointRadius.
          const clearLeading = extendsRight ? gp[0] < x0 - labelPointGap : gp[0] > x1 + labelPointGap;
          const clearTrailing = extendsRight ? gp[0] > x1 + pointRadius : gp[0] < x0 - pointRadius;
          if (clearLeading || clearTrailing) {
            continue;
          }
          const candidate = extendsRight ? gp[0] + labelPointGap : gp[0] - labelPointGap;
          if (pushTo === null || (extendsRight ? candidate > pushTo : candidate < pushTo)) {
            pushTo = candidate;
          }
        }

        if (pushTo === null || label.point[0] === pushTo) {
          break;
        }
        label.point[0] = pushTo;
        anyMoved = true;
      }
    }

    return anyMoved;
  };

  // Two labels only form a double point if their values share a sign. A pair whose values have
  // different signs (a jump through zero) - or where one value is zero - is NOT a double point and
  // must not be joined into a single "x, y" label. parseFloat reads the first number of a label,
  // which is enough to know its sign even after an earlier join produced an "x, y" text.
  const isSameSignDoublePoint = (a: (typeof deferredLabels)[number], b: (typeof deferredLabels)[number]): boolean => {
    const va = parseFloat(a.text);
    const vb = parseFloat(b.text);
    return Number.isFinite(va) && Number.isFinite(vb) && va * vb > 0;
  };

  type LabelEntry = (typeof deferredLabels)[number];

  // Resolve label collisions for one phase. `considerPair` selects which label pairs this phase
  // looks at; `movable` decides which labels this phase is allowed to reposition; `avoidCircle`
  // selects which data-point circles a moved label must keep off (so, e.g., placing V/M does not
  // dodge the N/D circles). A non-movable label is pinned to its current side, so the resolver
  // only moves the labels of the phase.
  const resolveCollisions = (
    considerPair: (a: LabelEntry, b: LabelEntry) => boolean,
    movable: (l: LabelEntry) => boolean,
    avoidCircle: (mover: LabelEntry, obstacle: LabelEntry) => boolean,
  ) => {
    const sidesFor = (label: LabelEntry): readonly ("left" | "right")[] =>
      movable(label) ? (["left", "right"] as const) : [currentSide(label)];

    for (let iteration = 0; iteration < 5; iteration++) {
      let moved = false;
      for (let i = 0; i < deferredLabels.length; i++) {
        for (let j = i + 1; j < deferredLabels.length; j++) {
          const a = deferredLabels[i];
          const b = deferredLabels[j];
          if (!considerPair(a, b)) {
            continue;
          }
          const isSameDataSet = a.type === b.type;

          // Same data set double point (e.g. the two V-values at a jump): this is handled
          // UNCONDITIONALLY, not only on collision - otherwise a jump through zero (one positive
          // and one negative value) would leave one label on each side of the double point. The
          // two values are joined into ONE label, separated by a comma and a space (the partner
          // label is emptied and not drawn - its data point remains). Both values are placed to
          // the LEFT of the data point if positive and to the RIGHT if negative (decided by the
          // sum, so the dominant value wins at a sign change).
          // ...but only bundle them when their labels would actually overlap. A large jump (e.g. a
          // shear step at a point load, -18.71 -> -68.71) puts the two circles far apart with plenty
          // of room to label each point separately, so it must NOT be collapsed into one "x, y"
          // label - that would leave the inner point unlabelled and look like it is missing.
          const circleGap = Math.abs(a.graphPoint[0] - b.graphPoint[0]);
          const labelsWouldOverlap = circleGap < (labelWidth(a) + labelWidth(b)) / 2 + 2 * labelPointGap;
          const isDoublePointPair =
            isSameDataSet &&
            Math.abs(a.graphPoint[1] - b.graphPoint[1]) < 0.0021 * scale * 1.5 &&
            isSameSignDoublePoint(a, b) &&
            labelsWouldOverlap;
          if (isDoublePointPair && a.text !== "") {
            let changed = false;

            if (b.text !== "") {
              // A pair with EQUAL formatted values is not a real jump - show the value once
              // instead of a joined "x, x" label. Joined labels are only for differing values.
              a.text = b.text === a.text ? a.text : `${a.text}, ${b.text}`;
              b.text = "";
              changed = true;
            }

            const sum = a.text
              .split(",")
              .map((s) => parseFloat(s))
              .reduce((acc, v) => acc + (Number.isFinite(v) ? v : 0), 0);
            // Sign rule as in the default schema (same rule for every diagram type). `sum` comes
            // from the label TEXT, which is the raw value, so DRAW_SIGN has to be re-applied to get
            // the DRAWN sign that decides which side of the zero axis the pair actually sits on.
            const preferredSide: "left" | "right" = sum * DRAW_SIGN[a.type] < 0 ? "right" : "left";

            // A datapoint from another dataset WITHIN the span of the double points: the joined
            // label goes to its sign-rule side and the middle point's label is moved to the
            // opposite side - but only if this phase is allowed to move that middle label.
            const spanMin = Math.min(a.graphPoint[0], b.graphPoint[0]);
            const spanMax = Math.max(a.graphPoint[0], b.graphPoint[0]);
            const middleLabels = deferredLabels.filter(
              (other) =>
                other !== a &&
                other !== b &&
                other.type !== a.type &&
                other.type !== "D" &&
                a.type !== "D" &&
                other.text !== "" &&
                movable(other) &&
                Math.abs(other.graphPoint[1] - a.graphPoint[1]) < labelTextHalfHeight &&
                other.graphPoint[0] > spanMin &&
                other.graphPoint[0] < spanMax,
            );

            const doublePointOffsetX = 0.08; // keep in sync with labelAnchorX
            const spanLeftX = Math.min(a.graphPoint[0], b.graphPoint[0]);
            const spanRightX = Math.max(a.graphPoint[0], b.graphPoint[0]);

            let side: "left" | "right";
            if (middleLabels.length > 0) {
              side = preferredSide;
              const middleSide: "left" | "right" = side === "right" ? "left" : "right";
              const middleAnchorX =
                middleSide === "right" ? spanRightX + doublePointOffsetX : spanLeftX - doublePointOffsetX;
              const middleAlign = middleSide === "right" ? ("left" as const) : ("right" as const);
              for (const middle of middleLabels) {
                if (middle.point[0] !== middleAnchorX || middle.horiAlign !== middleAlign) {
                  middle.point[0] = middleAnchorX;
                  middle.horiAlign = middleAlign;
                  changed = true;
                }
              }
            } else {
              // if a foreign data point blocks the preferred side the other side is used (the
              // partner's own data point is excluded - the two points sit right next to each other)
              side = collidesWithDataPoint(a, preferredSide, avoidCircle, b)
                ? preferredSide === "left"
                  ? "right"
                  : "left"
                : preferredSide;
            }

            const anchorX = side === "right" ? spanRightX + doublePointOffsetX : spanLeftX - doublePointOffsetX;
            const anchorAlign = side === "right" ? ("left" as const) : ("right" as const);

            if (a.point[0] !== anchorX || a.horiAlign !== anchorAlign) {
              a.point[0] = anchorX;
              a.horiAlign = anchorAlign;
              changed = true;
            }

            moved = changed || moved;
            continue;
          }

          // labels emptied by a double point join are not drawn and take no part in collisions
          if (a.text === "" || b.text === "") {
            continue;
          }

          // Same data set, but NOT a double point pair: two adjacent labels on a steep part of the
          // curve can collide. Move the movable label whose data point is closest to its own zero
          // axis to the other side of its data point; the other label stays where it is.
          if (isSameDataSet) {
            const [sMinA, sMaxA] = labelXInterval(a);
            const [sMinB, sMaxB] = labelXInterval(b);
            const sameSetOverlapX = sMinA < sMaxB && sMinB < sMaxA;
            const sameSetOverlapY = Math.abs(a.point[1] - b.point[1]) < 2 * labelTextHalfHeight;

            if (sameSetOverlapX && sameSetOverlapY) {
              const distA = Math.abs(a.graphPoint[0] - a.laneX);
              const distB = Math.abs(b.graphPoint[0] - b.laneX);
              const toMove = distA <= distB ? a : b;
              if (movable(toMove)) {
                const otherSide: "left" | "right" = currentSide(toMove) === "left" ? "right" : "left";
                moved = snapLabelToSide(toMove, otherSide) || moved;
              }
            }
            continue;
          }

          // Cross-type collision, decided from the actual text boxes: the x-intervals must overlap
          // AND the labels must be close enough vertically. A pair is also in conflict when one
          // label's container covers the OTHER label's data point.
          const [aMin, aMax] = labelXInterval(a);
          const [bMin, bMax] = labelXInterval(b);
          const overlapX = aMin < bMax && bMin < aMax;
          const overlapY = Math.abs(a.point[1] - b.point[1]) < 2 * labelTextHalfHeight;

          const coversDataPointOf = (label: LabelEntry, other: LabelEntry): boolean => {
            const [x0, x1] = labelXInterval(label);
            const r = graph_point_style?.radius ?? 0; // the data point CIRCLE, not just its centre
            return (
              other.graphPoint[0] >= x0 - r &&
              other.graphPoint[0] <= x1 + r &&
              Math.abs(other.graphPoint[1] - label.graphPoint[1]) < labelTextHalfHeight + r
            );
          };

          if ((overlapX && overlapY) || coversDataPointOf(a, b) || coversDataPointOf(b, a)) {
            // Decide which sides of their data points the two labels should go to by scoring every
            // allowed side combination: separating the labels dominates, then keeping label
            // containers off data points, then the preferred orientation. Non-movable labels can
            // only stay on their current side.
            const isNCrossPair = a.type === "N" || b.type === "N";

            let first: LabelEntry;
            let second: LabelEntry;
            let preferredFirst: "left" | "right";
            let preferredSecond: "left" | "right";

            if (isNCrossPair) {
              // N prefers the left side of its data point, the V/M label the right side.
              first = a.type === "N" ? a : b;
              second = a.type === "N" ? b : a;
              preferredFirst = "left";
              preferredSecond = "right";
            } else {
              const distA = Math.abs(a.graphPoint[0] - a.laneX);
              const distB = Math.abs(b.graphPoint[0] - b.laneX);
              let toMove = distA <= distB ? a : b;
              if (distA === distB) {
                const priority = (t: DiagramType): number => (t === "M" ? 0 : t === "N" ? 1 : t === "V" ? 2 : 3);
                toMove = priority(a.type) <= priority(b.type) ? a : b;
              }
              first = toMove;
              second = toMove === a ? b : a;
              preferredFirst = "left";
              preferredSecond = currentSide(second);
            }

            let bestFirst: "left" | "right" = currentSide(first);
            let bestSecond: "left" | "right" = currentSide(second);
            let bestScore = -1;

            for (const s1 of sidesFor(first)) {
              for (const s2 of sidesFor(second)) {
                const [f0, f1] = intervalForSide(first, s1);
                const [g0, g1] = intervalForSide(second, s2);
                const separated = !(f0 < g1 && g0 < f1);

                let score = 0;
                if (separated) score += 100; // separating the labels dominates everything else
                if (!collidesWithDataPoint(first, s1, avoidCircle)) score += 40;
                if (!collidesWithDataPoint(second, s2, avoidCircle)) score += 40;
                if (s1 === preferredFirst) score += 10;
                if (s2 === preferredSecond) score += 20; // moving the second label is less preferred

                if (score > bestScore) {
                  bestScore = score;
                  bestFirst = s1;
                  bestSecond = s2;
                }
              }
            }

            if (movable(first)) moved = snapLabelToSide(first, bestFirst) || moved;
            if (movable(second)) moved = snapLabelToSide(second, bestSecond) || moved;
          }
        }
      }

      moved = enforceGapToDataPoints(movable, avoidCircle) || moved;
      if (!moved) {
        break;
      }
    }
  };

  const isForce = (l: LabelEntry): boolean => l.type === "V" || l.type === "M";
  const isNorD = (l: LabelEntry): boolean => l.type === "N" || l.type === "D";

  // Labels start at their original positions (set above). Collisions are then resolved in a fixed
  // order. Steps 1 and 2 place the V/M labels; AFTER step 2 the V/M labels are FROZEN - no later
  // step may move them. N and D are then adjusted around the fixed V/M labels and circles.

  // 1. V vs M (same lane) - both may move; only V/M circles are avoided, so the placement is NOT
  //    pulled around by the N or D data points.
  resolveCollisions(
    (a, b) => isForce(a) && isForce(b),
    (l) => isForce(l),
    (_mover, obstacle) => isForce(obstacle),
  );
  // 2. Swap a V/M label off an N or D circle: if the current side of a force label sits on an N/D
  //    data point and the other side is clear of N/D data points, flip it. ONLY N/D circles count
  //    here - V/M circles were already handled in step 1, and a plain label overlap does NOT cause
  //    a swap (only an actual N/D circle collision does). This is the last step that moves V/M.
  //
  //    Back-substitution: a swap here can push a V/M label onto its M/V partner. So after the swaps,
  //    V vs M is resolved once more with the SWAPPED labels pinned - the partner that the swap ran
  //    into is then free to move aside, while the label we deliberately moved off the N/D circle
  //    stays put.
  {
    const avoidNorDCircle = (_mover: LabelEntry, obstacle: LabelEntry): boolean => isNorD(obstacle);
    const swapped = new Set<LabelEntry>();
    for (const label of deferredLabels) {
      if (label.text === "" || !isForce(label)) {
        continue;
      }
      const cur = currentSide(label);
      if (!collidesWithDataPoint(label, cur, avoidNorDCircle)) {
        continue; // current side does not sit on an N/D circle - leave it exactly where step 1 put it
      }
      const other: "left" | "right" = cur === "left" ? "right" : "left";
      if (collidesWithDataPoint(label, other, avoidNorDCircle)) {
        continue; // both sides blocked by N/D circles - keep it on its sign-rule side
      }
      snapLabelToSide(label, other);
      swapped.add(label);
    }
    if (swapped.size > 0) {
      resolveCollisions(
        (a, b) => isForce(a) && isForce(b),
        (l) => isForce(l) && !swapped.has(l),
        (_mover, obstacle) => isForce(obstacle),
      );
    }
  }
  // 2b. Comma-join the M and V label at the same member position when they cannot both be shown
  //     cleanly: either they STILL overlap each other after the placement above, OR one of them
  //     still sits on an N/D circle (side-swapping in step 2 could not clear it). Instead of nudging
  //     them apart vertically, their two values become one label led by the value whose circle is
  //     furthest from the baseline (when the two circles coincide, the next point along the member
  //     decides); the other partner is emptied, and the combined label is anchored clear of both
  //     M/V points on whichever side is free of N/D circles (so it is pushed away from the N circle,
  //     not onto it). The emptied label keeps its data point, which N/D still avoid.
  {
    const offset = 0.08; // keep in sync with labelAnchorX
    const r = graph_point_style?.radius ?? 0;
    // does a combined label of the given width, anchored on `side` of the M/V span, cover an N/D circle?
    const combinedCoversNorD = (
      spanLeftX: number,
      spanRightX: number,
      side: "left" | "right",
      width: number,
      gy: number,
    ): boolean => {
      const anchorX = side === "right" ? spanRightX + offset : spanLeftX - offset;
      const x0 = side === "right" ? anchorX : anchorX - width;
      const x1 = side === "right" ? anchorX + width : anchorX;
      return deferredLabels.some(
        (o) =>
          isNorD(o) &&
          o.graphPoint[0] >= x0 - r &&
          o.graphPoint[0] <= x1 + r &&
          Math.abs(o.graphPoint[1] - gy) < labelTextHalfHeight + r,
      );
    };
    const onNorDCircle = (label: LabelEntry): boolean =>
      collidesWithDataPoint(label, currentSide(label), (_m, o) => isNorD(o));

    // Ordering a comma-joined M+V pair uses how far each circle sits from its baseline: the value
    // whose circle is FURTHEST from the baseline is shown first. When the two circles (near) coincide
    // at a position - so there is no readable "furthest" - fall back to the NEXT point along the
    // member and lead with whichever is furthest there. Circle x and laneX are unaffected by joining.
    const distAt = new Map<number, { M?: number; V?: number }>();
    for (const l of deferredLabels) {
      if (!isForce(l) || l.text === "") continue;
      const d = Math.abs(l.graphPoint[0] - l.laneX);
      const entry = distAt.get(l.graphPoint[1]) ?? {};
      entry[l.type as "M" | "V"] = d;
      distAt.set(l.graphPoint[1], entry);
    }
    // member positions (as graphPoint[1]) that carry BOTH an M and a V, in member order
    const bothPositions = [...distAt.entries()]
      .filter(([, e]) => e.M !== undefined && e.V !== undefined)
      .map(([gy]) => gy)
      .sort((p, q) => p - q);
    const furthestTypeAtNext = (gy: number): "M" | "V" => {
      const idx = bothPositions.indexOf(gy);
      // the NEXT point (fall back to the previous one at the far end)
      const neighbourGy =
        idx >= 0 && bothPositions.length > 1 ? bothPositions[idx + 1 < bothPositions.length ? idx + 1 : idx - 1] : gy;
      const e = distAt.get(neighbourGy) ?? distAt.get(gy);
      if (e && e.M !== undefined && e.V !== undefined) {
        return e.V >= e.M ? "V" : "M";
      }
      return "V";
    };

    const forceLabels = deferredLabels.filter((l) => isForce(l) && l.text !== "");
    for (let i = 0; i < forceLabels.length; i++) {
      for (let j = i + 1; j < forceLabels.length; j++) {
        const a = forceLabels[i];
        const b = forceLabels[j];
        if (a.text === "" || b.text === "" || a.type === b.type) {
          continue; // only M+V pairs, and skip already-joined labels
        }
        if (Math.abs(a.graphPoint[1] - b.graphPoint[1]) >= labelTextHalfHeight) {
          continue; // not the same member position
        }
        const [aMin, aMax] = labelXInterval(a);
        const [bMin, bMax] = labelXInterval(b);
        const overlapX = aMin < bMax && bMin < aMax;
        const overlapY = Math.abs(a.point[1] - b.point[1]) < 2 * labelTextHalfHeight;
        const mvOverlap = overlapX && overlapY;
        // join if they overlap each other, or if either one is still stuck on an N/D circle
        if (!mvOverlap && !onNorDCircle(a) && !onNorDCircle(b)) {
          continue;
        }

        // Order the pair: the value whose circle is furthest from the baseline comes first. If the
        // two circles (near) coincide, there is no readable order - defer to the next point.
        const sep = Math.abs(a.graphPoint[0] - b.graphPoint[0]);
        const coincidentTol = Math.max(0.04, 2 * r);
        let firstLabel: LabelEntry;
        if (sep > coincidentTol) {
          const distA = Math.abs(a.graphPoint[0] - a.laneX);
          const distB = Math.abs(b.graphPoint[0] - b.laneX);
          firstLabel = distA >= distB ? a : b;
        } else {
          firstLabel = a.type === furthestTypeAtNext(a.graphPoint[1]) ? a : b;
        }
        const secondLabel = firstLabel === a ? b : a;
        const combined = `${firstLabel.text}, ${secondLabel.text}`;
        const combinedWidth = combined.length * labelCharWidth;

        const dominant = firstLabel;
        const spanLeftX = Math.min(a.graphPoint[0], b.graphPoint[0]);
        const spanRightX = Math.max(a.graphPoint[0], b.graphPoint[0]);
        const gy = a.graphPoint[1];

        // Prefer the dominant value's sign side, but only if it is free of N/D circles; otherwise use
        // the side that is free (so the label is pushed away from, e.g., the N circle on the left).
        const domSide = currentSide(dominant);
        const altSide: "left" | "right" = domSide === "left" ? "right" : "left";
        let side: "left" | "right" = domSide;
        if (
          combinedCoversNorD(spanLeftX, spanRightX, domSide, combinedWidth, gy) &&
          !combinedCoversNorD(spanLeftX, spanRightX, altSide, combinedWidth, gy)
        ) {
          side = altSide;
        }

        const anchorX = side === "right" ? spanRightX + offset : spanLeftX - offset;
        a.text = combined;
        b.text = "";
        a.point[0] = anchorX;
        a.point[1] = gy;
        a.horiAlign = side === "right" ? "left" : "right";
      }
    }
  }
  // 3. N vs V/M labels/circles (and N vs N) - only the N labels move.
  resolveCollisions(
    (a, b) => (a.type === "N" || b.type === "N") && a.type !== "D" && b.type !== "D",
    (l) => l.type === "N",
    (_mover, obstacle) => isForce(obstacle) || obstacle.type === "N",
  );
  // 4. D vs V/M labels/circles (and D vs D) - only the D labels move. (D never meets N: V/M sits
  //    between them.)
  resolveCollisions(
    (a, b) => (a.type === "D" || b.type === "D") && (isForce(a) || isForce(b) || a.type === b.type),
    (l) => l.type === "D",
    (_mover, obstacle) => isForce(obstacle) || obstacle.type === "D",
  );

  // Second pass: draw all text labels on top of all curves so they are never obscured. Each
  // label is tinted in its own diagram's line color (matching the lane header above) so a value
  // is unambiguous even when M/V/N/D labels end up close together.
  const labelLineColorForType: Record<DiagramType, string | undefined> = {
    M: moment_line_color_style.lineColor,
    V: shear_line_color_style.lineColor,
    N: normal_line_color_style.lineColor,
    D: deformation_line_color_style.lineColor,
  };
  for (const label of deferredLabels) {
    if (label.text === "") {
      continue; // emptied by a double point join
    }
    g.drawText(
      label.point,
      label.text,
      { ...label_style, lineColor: labelLineColorForType[label.type] ?? label_style.lineColor },
      {
        horiAlign: label.horiAlign,
        vertAlign: label.vertAlign,
        display: visible,
      },
    );
  }

  // Third pass: one header per lane, centered over the lane's zero axis, laid out by
  // layoutDiagramHeader. vertAlign "bottom" puts the text above the given point, i.e. clear of the
  // diagram.
  const headerFontSize = typeof diagram_header_style.textSize === "number" ? diagram_header_style.textSize : 12;
  const headerOffsetY = 0.12; // distance in drawing units from the top of the diagram up to the header

  for (const header of laneHeaders.values()) {
    for (const run of layoutDiagramHeader(header.segments, direction, headerFontSize)) {
      g.drawText(
        [header.laneX, diagramTopY - headerOffsetY],
        run.text,
        { ...diagram_header_style, lineColor: run.color, textSize: run.fontSize },
        {
          horiAlign: "left",
          vertAlign: "bottom",
          horiOffset: run.horiOffset,
          vertOffset: run.vertOffset,
          display: visible,
        },
      );
    }
  }

  return diagramValueRanges;
};

/**
 * The value range per diagram type of the *2nd order* results, in the same units the diagrams are
 * drawn in - or an empty set when the output holds no 2nd order results.
 *
 * The diagrams are normalised by one shared range per type (see SideView's sizing pass), but the
 * result dictionaries App hands down only ever hold the order currently on screen: flipping the
 * 1st/2nd toggle rebuilds them. Scaling each order by its own maximum makes the 2nd order diagram
 * *shrink* whenever some other load combination gained more from the 2nd order run than the one
 * being looked at - larger numbers, smaller picture. Taking the shared range from the 2nd order
 * results in both cases removes that: the 2nd order values are the larger ones, so the 1st order
 * diagram is drawn smaller than the 2nd order one, which is the way round a reader expects.
 *
 * Read straight off the output rather than through the dictionaries, because the 2nd order set is
 * not materialised at all while the toggle sits on "1st".
 *
 * The zero threshold is a property of one curve - CreateGraphValues flattens a curve whose every
 * value is below it - so it is applied per load combination and element, before folding into the
 * shared maximum, exactly as the drawing pass would.
 */
export const secondOrderDiagramValueRanges = (output: ng.output.Data | null | undefined): DiagramValueRanges => {
  const ranges: DiagramValueRanges = {};

  const fold = (type: DiagramType, maxAbsValue: number, zero: number) => {
    if (!Number.isFinite(maxAbsValue) || maxAbsValue < zero) return;
    ranges[type] = Math.max(ranges[type] ?? 0, maxAbsValue);
  };

  for (const lcomb of output?.lcombs ?? []) {
    for (const elementResult of lcomb.elementResults ?? []) {
      let maxM = 0;
      let maxV = 0;
      let maxN = 0;
      let maxD = 0;

      for (const position of elementResult.result?.positions ?? []) {
        const second = position.results2nd;
        if (!second) continue;

        const force = second.force;
        for (let i = 0; i < (force?.type?.length ?? 0); i++) {
          const value = Math.abs(Number(force?.value?.[i]));
          if (!Number.isFinite(value)) continue;
          switch (force?.type?.[i]) {
            case ng.result.Force.FORCE_N:
              maxN = Math.max(maxN, value);
              break;
            // The sizing pass takes the maximum over both view directions, and xy/xz plot
            // different components of the same diagram type (V1/V2, M1/M2), so both feed one max.
            case ng.result.Force.FORCE_V1:
            case ng.result.Force.FORCE_V2:
              maxV = Math.max(maxV, value);
              break;
            case ng.result.Force.FORCE_M1:
            case ng.result.Force.FORCE_M2:
              maxM = Math.max(maxM, value);
              break;
            default:
              break;
          }
        }

        const displacement = second.displacement;
        for (let i = 0; i < (displacement?.type?.length ?? 0); i++) {
          const value = Math.abs(Number(displacement?.value?.[i]));
          if (!Number.isFinite(value)) continue;
          switch (displacement?.type?.[i]) {
            // The D diagram plots uy (xy) and uz (xz) - the same two the dictionaries carry as
            // IDisplacement.y / .z.
            case ng.result.Displacement.DISPLACEMENT_Y:
            case ng.result.Displacement.DISPLACEMENT_Z:
              maxD = Math.max(maxD, value);
              break;
            default:
              break;
          }
        }
      }

      fold("M", maxM * FORCE_VALUE_SCALE, FORCE_ZERO);
      fold("V", maxV * FORCE_VALUE_SCALE, FORCE_ZERO);
      fold("N", maxN * FORCE_VALUE_SCALE, FORCE_ZERO);
      fold("D", maxD * DISPLACEMENT_VALUE_SCALE, DISPLACEMENT_ZERO);
    }
  }

  return ranges;
};

/**
 * A value range per diagram type that stays the same across every load combination, both view
 * directions, and the 1st/2nd order toggle - the isometric views' counterpart of the sizing pass
 * SideView runs for its own 2D diagrams (see the comment above its `diagramValueRanges`).
 *
 * Without this, the isometric result boards independently normalize whichever curve is currently
 * selected to fill the full board amplitude (see drawDiagram / drawResultBoard in
 * SvgIsometricColumnView.tsx and the equivalent loop in ThreeColumnView.tsx), so a smaller-value
 * curve is stretched to the same visible height as a larger one - switching load combination,
 * view direction or the 1st/2nd order toggle changes the plotted shape but never the drawn size,
 * which looks like nothing changed at all.
 *
 * A null drawer measures without drawing; DrawAllGraphs runs the exact same graph-value
 * generation the real render does, so the ranges come from exactly the values later drawn. Like
 * SideView, this is then widened with the 2nd order ranges read straight off the project output,
 * which stays available even when the toggle sits on "1st" and the 2nd order dictionaries are not
 * materialised at all.
 */
export const isometricDiagramValueRanges = (
  results: DiagramResults,
  graphPointsMode: GraphPointsMode,
  output: ng.output.Data | null | undefined,
): DiagramValueRanges => {
  const diagramTypes: DiagramType[] = ["M", "V", "N", "D"];
  const valueRanges: DiagramValueRanges = {};
  const lcombGuids = [...new Set([...Object.keys(results.elementForces), ...Object.keys(results.displacements)])];

  for (const lcombGuid of lcombGuids) {
    for (const state of ["xy", "xz"] as GraphDirection[]) {
      const measured = DrawAllGraphs(null, {
        showMoment: true,
        showShear: true,
        showAxial: true,
        showDeformations: true,
        graphPointsMode,
        results,
        state,
        selectedLcombGuid: lcombGuid,
        visible: false,
        horOffset: 0,
      });
      for (const type of diagramTypes) {
        valueRanges[type] = Math.max(valueRanges[type] ?? 0, measured[type] ?? 0);
      }
    }
  }

  const secondOrderRanges = secondOrderDiagramValueRanges(output);
  for (const type of diagramTypes) {
    valueRanges[type] = Math.max(valueRanges[type] ?? 0, secondOrderRanges[type] ?? 0);
  }

  return valueRanges;
};

export const CreateGraphValues = (
  resultPositions: ISectionForce[],
  displacements: IDisplacement[],
  direction: GraphDirection,
  diagramType: DiagramType,
  scale: number,
): ValueType[] => {
  // SIGN CONVENTION - do not add ad-hoc sign flips here; use DRAW_SIGN (top of the file).
  //
  // `verticalValue` is the raw analysis value, unit-scaled and then multiplied by the diagram
  // type's DRAW_SIGN - nothing else. The raw quantity per diagram type is:
  //   N -> N, V -> V1 (xy) / V2 (xz), M -> M2 (xy) / M1 (xz), D -> uy (xy) / uz (xz)
  // The engine convention is dM2/dx = V1 and dM1/dx = V2.
  //
  // Labels undo DRAW_SIGN again (see labelSign in DrawGraph), so the printed number is always the
  // raw analysis value and always matches the code check - only the drawn side changes.
  //
  // Which SIDE of the zero axis a value ends up on is otherwise a property of the shared renderer:
  // draw_graph maps a point to screen as [-verticalValue, -horizontalValue], so a positive drawn
  // value goes towards -y (xy view) / -z (xz view) - i.e. to the left, since the viewport draws
  // the y/z axis pointing right.

  // Check if results are available
  let nbrOfResults = resultPositions ? resultPositions.length : 0;
  if (nbrOfResults === 0) {
    return [];
  }

  // Scales for converting values to graph coordinates
  let horizontal_value_scale = 1.0; // [m]
  horizontal_value_scale *= scale;

  // Loop through results and create values for the graph
  const values = new Array<ValueType>();
  if (diagramType === "D") {
    const value_scale = DISPLACEMENT_VALUE_SCALE * scale; // [mm]
    const zero = DISPLACEMENT_ZERO;

    for (let i = 0; i < displacements.length; i++) {
      let pos = displacements[i];
      let position = pos.pos * horizontal_value_scale; // position along the element
      if (!Number.isFinite(position)) continue;
      let displacement = 0; // deflection value to be plotted at that position
      switch (direction) {
        case "xy":
          displacement = pos.y.max * value_scale * DRAW_SIGN.D;
          break;
        case "xz":
          displacement = pos.z.max * value_scale * DRAW_SIGN.D;
          break;
      }
      if (!Number.isFinite(displacement)) continue;
      values.push({ horizontalValue: position, verticalValue: displacement });
    }
    // If all values are below the threshold, zero them all out
    if (values.every((v) => Math.abs(v.verticalValue) < zero)) {
      for (const v of values) v.verticalValue = 0;
    }
  } else {
    const value_scale = FORCE_VALUE_SCALE * scale; // [kNm] or [kN]
    const zero = FORCE_ZERO;

    for (let i = 0; i < resultPositions.length; i++) {
      let pos = resultPositions[i];
      let position = pos.pos * horizontal_value_scale;
      if (!Number.isFinite(position)) continue;
      let force = 0;
      switch (diagramType) {
        case "M":
          force = (direction === "xy" ? pos.M2.max : pos.M1.max) * value_scale * DRAW_SIGN.M;
          break;
        case "V":
          force = (direction === "xy" ? pos.V1.max : pos.V2.max) * value_scale * DRAW_SIGN.V;
          break;
        case "N":
          force = pos.N.max * value_scale * DRAW_SIGN.N;
          break;
      }
      if (!Number.isFinite(force)) continue;
      values.push({ horizontalValue: position, verticalValue: force });
    }
    // If all values are below the threshold, zero them all out
    if (values.every((v) => Math.abs(v.verticalValue) < zero)) {
      for (const v of values) v.verticalValue = 0;
    }
  }

  // Sort values by horizontal coordinate (to be sure)
  values.sort((a, b) => a.horizontalValue - b.horizontalValue);

  return values;
};

/**
 * Reserve the horizontal room the diagram lanes can take up, whatever values they hold.
 *
 * The view is auto-fitted to the bounding box of everything drawn (create_camera in Rendering), so
 * the column and the diagram zero axes only stay put as long as that box does. A curve is
 * normalised against a range shared by every load combination and by both result orders, which
 * means a curve reaches its lane edge (+/-1) only when it is the one the range was taken from:
 * after a 2nd order run every 1st order curve is drawn well short of it. Left alone the box would
 * narrow on switching 2nd -> 1st and the whole view would shift and rescale - the column moving
 * because the diagrams next to it got smaller.
 *
 * One transparent, zero-width diagonal from the outer edge of the first lane to the outer edge of
 * the last pins the box instead. The reserve past +/-1 is room for the value label that sits just
 * outside an extreme point; without it the box would still breathe by a label's width, since a
 * label follows its data point inwards as the curve shrinks. It is an ordinary drawable rather
 * than a hidden one, so it holds whether or not hidden drawables are counted into the box.
 *
 * Only the outermost edges actually matter - the box is axis aligned, so lanes in between cannot
 * widen it - but both ends are anchored so the layout can grow a lane at either side.
 */
export const drawDiagramExtentAnchors = (
  g: Drawer<ArrowId>,
  props: {
    showMoment: boolean;
    showShear: boolean;
    showAxial: boolean;
    showDeformations: boolean;
    horOffset: number;
    columnHeight: number;
  },
): void => {
  // Same lanes, in the same order, as DrawGraph builds: N first, M and V sharing the next, D last.
  const laneCount =
    (props.showAxial ? 1 : 0) + (props.showMoment || props.showShear ? 1 : 0) + (props.showDeformations ? 1 : 0);
  if (laneCount === 0 || !Number.isFinite(props.columnHeight)) {
    return;
  }

  const halfWidth = 1 + diagramLabelReserve;
  const left = props.horOffset - halfWidth;
  const right = props.horOffset + diagramPadding * (laneCount - 1) + halfWidth;

  g.drawLine([left, 0], [right, -props.columnHeight], extent_anchor_style);
};

export const DrawAllGraphs = (
  g: Drawer<ArrowId> | null,
  props: {
    showMoment: boolean;
    showShear: boolean;
    showAxial: boolean;
    showDeformations: boolean;
    graphPointsMode: GraphPointsMode;
    results: DiagramResults;
    state: GraphDirection;
    selectedLcombGuid: string;
    visible: boolean;
    horOffset: number;
    sharedValueRanges?: DiagramValueRanges;
  },
): DiagramValueRanges => {
  const allDiagramTypes: (DiagramType | null)[] = [
    props.showMoment ? "M" : null,
    props.showShear ? "V" : null,
    props.showAxial ? "N" : null,
    props.showDeformations ? "D" : null,
  ];
  const selectedDiagramTypes = allDiagramTypes.filter((x) => x !== null);

  let resultPositions: ISectionForce[] = [];
  let displacements: IDisplacement[] = [];

  if (Object.keys(props.results.elementForces).length > 0) {
    const lcRes = props.results.elementForces[props.selectedLcombGuid];
    const elemRes = lcRes ? Object.values(lcRes)[0] : null;
    if (lcRes && elemRes) {
      resultPositions = elemRes;
    }
  }

  if (Object.keys(props.results.displacements).length > 0) {
    const lcRes = props.results.displacements[props.selectedLcombGuid];
    const elemRes = lcRes ? Object.values(lcRes)[0] : null;
    if (lcRes && elemRes) {
      displacements = elemRes;
    }
  }

  let secondaryResultPositions: ISectionForce[] = [];
  let secondaryDisplacements: IDisplacement[] = [];

  if (props.results.elementForcesSecondary && Object.keys(props.results.elementForcesSecondary).length > 0) {
    const lcRes = props.results.elementForcesSecondary[props.selectedLcombGuid];
    const elemRes = lcRes ? Object.values(lcRes)[0] : null;
    if (lcRes && elemRes) {
      secondaryResultPositions = elemRes;
    }
  }

  if (props.results.displacementsSecondary && Object.keys(props.results.displacementsSecondary).length > 0) {
    const lcRes = props.results.displacementsSecondary[props.selectedLcombGuid];
    const elemRes = lcRes ? Object.values(lcRes)[0] : null;
    if (lcRes && elemRes) {
      secondaryDisplacements = elemRes;
    }
  }

  return DrawGraph(
    g,
    resultPositions,
    displacements,
    props.state,
    selectedDiagramTypes,
    props.graphPointsMode,
    1,
    props.visible,
    props.horOffset,
    props.sharedValueRanges,
    secondaryResultPositions,
    secondaryDisplacements,
  );
};
